// @givmo/board — the Form 990 Part VI line-8 clock, rendered as the backend wrote it.
//
// The instructions describe documentation as contemporaneous when prepared by the LATER of the next
// meeting of the governing body or 60 days after the meeting or written action. The backend does that
// arithmetic in `app/domain/board_contemporaneity.py` and renders ONE SENTENCE carrying the rule and
// the dates it applied it to.
//
// ⚠ **THAT SENTENCE IS SHOWN VERBATIM AND NOTHING IS COMPOSED BESIDE IT.** A paraphrase of an
// instruction is a statement about what the instruction requires, which is §3.2's Band-3 line and
// §3.4's `meets-irs-requirements` ban. The dates are shown as figures because they are the organization's
// own dates; the meaning of those dates stays in the backend's own words.
//
// ⚠ **AND `past_the_date` IS NOT A FINDING.** The states are named for what is true of the RECORD and
// the DATE — never for a verdict. `past_the_date` says the date has passed; it does not say the
// organization failed anything, nothing here says so either, and the tone never reaches `danger`, because
// a red badge on a governance screen reads as a finding when there is none to report.
//
// Two states worth knowing while reading this component:
//
//   * with NO next meeting recorded, the sentence says the date can still MOVE — presenting the
//     60-day date as final would state the instruction's own rule inaccurately, because the other arm
//     has simply not been supplied;
//   * an UPCOMING meeting has no clock at all. The clock starts when the meeting is held.

import type { BoardContemporaneityOut } from '@givmo/api-client';
import { Badge, Card, CardBody } from '@givmo/design-system';
import { displayDate } from '../../lib/display';
import { clockStateLabel, clockStatement, clockTone } from './minutes-decode';

export interface ContemporaneityBlockProps {
  clock: BoardContemporaneityOut | null | undefined;
}

export function ContemporaneityBlock({ clock }: ContemporaneityBlockProps) {
  const statement = clockStatement(clock);
  // No clock is no card. An upcoming meeting genuinely has none, and an empty panel with dashes would
  // imply a date exists that is being withheld.
  if (clock === null || clock === undefined || statement === null) return null;

  return (
    <Card testId="contemporaneity-block">
      <CardBody className="flex flex-col gap-10">
        <div className="flex flex-wrap items-center gap-8">
          <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
            Writing these up
          </h2>
          <Badge tone={clockTone(clock.state)}>{clockStateLabel(clock.state)}</Badge>
        </div>

        {/* The backend's own sentence — the rule and the dates it applied it to, in its words. */}
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
          data-testid="contemporaneity-statement"
        >
          {statement}
        </p>

        <dl className="flex flex-wrap gap-x-24 gap-y-6" data-testid="contemporaneity-dates">
          {clock.anchor_on ? (
            <ClockDate label="The meeting" value={clock.anchor_on} testId="anchor" />
          ) : null}
          {clock.next_meeting_on ? (
            <ClockDate
              label="Your next meeting"
              value={clock.next_meeting_on}
              testId="next-meeting"
            />
          ) : null}
          {clock.sixty_day_on ? (
            <ClockDate label="60 days after" value={clock.sixty_day_on} testId="sixty-day" />
          ) : null}
          {clock.deadline_on ? (
            <ClockDate label="The later of the two" value={clock.deadline_on} testId="deadline" />
          ) : null}
          {clock.recorded_on ? (
            <ClockDate label="Recorded" value={clock.recorded_on} testId="recorded" />
          ) : null}
        </dl>
      </CardBody>
    </Card>
  );
}

function ClockDate({ label, value, testId }: { label: string; value: string; testId: string }) {
  return (
    <div className="flex flex-col gap-2">
      <dt className="font-sans text-s12 font-w600 tracking-givmo text-text-tertiary">{label}</dt>
      {/* A bare calendar date, rendered through the shared helper so it is not moved by a time zone —
          these are dates the organization recorded, and the 60-day date is arithmetic over one of them. */}
      <dd
        className="font-sans text-s13 font-w400 tracking-givmo text-text-strong"
        data-testid={`contemporaneity-${testId}`}
      >
        {displayDate(value)}
      </dd>
    </div>
  );
}
